import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import { EMPTY_REFERENCE, flightKey, type PnrReference } from "../components/pnr-display/pnr-itinerary-lib";
import { useClaim } from "./use-claim";

/**
 * Airports (name, city, timezone) and airlines behind the codes of a PNR image, read from the
 * claim's reservation — the same query the Réservation tab runs, so the cache is shared.
 */
export function usePnrReference(claimId: string): PnrReference {
  const { data: claim } = useClaim(claimId);
  const reservationId = claim?.legalSpecification.impactedSegment.reservationId ?? null;

  const { data } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "reservation-itinerary", reservationId],
    queryFn: async () => {
      const reservation = await adminAPI.retrieveReservationItinerary(reservationId ?? "");
      return reservation.segments?.filter((segment) => segment.cancelledBy !== "CUSTOMER");
    },
    enabled: reservationId !== null,
  });

  if (!data) {
    return EMPTY_REFERENCE;
  }

  const reference: PnrReference = { airports: {}, airlines: {}, flights: {} };
  for (const segment of data) {
    const { flight } = segment;
    for (const airport of [flight.departureAirport, flight.arrivalAirport]) {
      reference.airports[airport.iata] = {
        name: airport.name,
        nameFr: airport.frTranslation?.name ?? null,
        city: airport.city,
        cityFr: airport.frTranslation?.city ?? null,
        timezone: airport.timezone,
      };
    }
    reference.airlines[flight.airline.iata] = {
      name: flight.airline.name,
      iconUrl: flight.airline.legalData?.iconURL ?? null,
    };

    const identifier = flight.flightIdentifier.match(/^([A-Z0-9]{2})\s?(\d+)$/);
    const localDate = DateTime.fromJSDate(new Date(flight.scheduledDepartureUTC), {
      zone: flight.departureAirport.timezone,
    }).toISODate();
    const key = identifier ? flightKey(identifier[1], identifier[2], localDate) : null;
    if (key) {
      reference.flights[key] = {
        monitoringStatus: flight.monitoringStatus ?? null,
        actualDepartureUtc: flight.actualDepartureUTC
          ? new Date(flight.actualDepartureUTC).toISOString()
          : null,
        actualArrivalUtc: flight.inGateArrivalUTC ? new Date(flight.inGateArrivalUTC).toISOString() : null,
      };
    }
  }

  return reference;
}
